import { create } from 'zustand';
import { Adb } from '@yume-chan/adb';
import { AdbDaemonWebUsbDevice } from '@yume-chan/adb-daemon-webusb';
import { webAdbService } from '@/services/webadb.service';
import { useLogStore } from './log.store';
import { useFileStore } from './files.store';

// 设备信息存储类型
interface SavedDevice {
  serial: string;
  name: string;
  connectedAt?: number;
}

const DEVICES_STORAGE_KEY = 'web_adb_saved_devices';

interface DeviceState {
  device: AdbDaemonWebUsbDevice | null;
  adb: Adb | null;
  isConnected: boolean;
  deviceName: string;
  isConnecting: boolean;
  savedDevices: SavedDevice[];
  deviceInfo: { model: string; androidVersion: string; storage: { used: string; total: string; percent: number }; battery: number; isRooted: boolean } | null;

  // Actions
  connect: (device?: AdbDaemonWebUsbDevice) => Promise<void>;
  disconnect: () => Promise<void>;
  autoConnect: () => Promise<void>;
  checkConnection: () => Promise<boolean>;
  reset: () => void;
  loadSavedDevices: () => void;
  saveConnectedDevice: (serial: string, name: string) => void;
  forgetDevice: (serial: string) => Promise<void>;
}

// Set up logging callback for WebAdbService
webAdbService.setLogCallback((message, level) => {
  // Map AdbLogLevel to LogStore LogLevel
  // AdbLogLevel: 'info' | 'success' | 'error' | 'warning'
  // LogStore LogLevel: 'info' | 'success' | 'warning' | 'error' | 'progress' | 'undo' | 'redo'
  useLogStore.getState().addLog(level || 'info', message);
});

// 辅助函数：读取已保存设备
function getSavedDevices(): SavedDevice[] {
  try {
    const stored = localStorage.getItem(DEVICES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export const useDeviceStore = create<DeviceState>((set, get) => ({
  device: null,
  adb: null,
  isConnected: false,
  deviceName: '未连接',
  isConnecting: false,
  savedDevices: getSavedDevices(),
  deviceInfo: null,

  loadSavedDevices: () => {
    set({ savedDevices: getSavedDevices() });
  },

  saveConnectedDevice: (serial: string, name: string) => {
    const devices = getSavedDevices();
    if (!devices.find(d => d.serial === serial)) {
      devices.push({ serial, name, connectedAt: Date.now() });
      localStorage.setItem(DEVICES_STORAGE_KEY, JSON.stringify(devices));
      set({ savedDevices: devices });
      useLogStore.getState().addLog('success', `设备 "${name}" 已保存`);
    }
  },

  forgetDevice: async (serial: string) => {
    let devices = getSavedDevices();
    const deviceToForget = devices.find(d => d.serial === serial);
    devices = devices.filter(d => d.serial !== serial);
    localStorage.setItem(DEVICES_STORAGE_KEY, JSON.stringify(devices));
    set({ savedDevices: devices });
    
    if (deviceToForget) {
      useLogStore.getState().addLog('info', `已忘记设备: ${deviceToForget.name}`);
      // 尝试取消浏览器配对
      try {
        const pairedDevices = await webAdbService.getPairedDevices();
        const usbDevice = pairedDevices.find(d => d.serial === serial);
        if (usbDevice && (usbDevice as any).forget) {
          await (usbDevice as any).forget();
          useLogStore.getState().addLog('info', '浏览器配对已取消');
        }
      } catch { /* ignore */ }
    }
  },

  connect: async (device) => {
    if (get().isConnecting) return;

    set({ isConnecting: true });
    try {
      let targetDevice = device;
      if (!targetDevice) {
        useLogStore.getState().addLog('info', '请使用 USB 线缆连接设备，然后在弹出窗口中选择设备');
        targetDevice = await webAdbService.requestDevice();
      }
      if (!targetDevice) {
        set({ isConnecting: false });
        return;
      }

      useLogStore.getState().addLog('info', '正在连接，请在手机端确认"允许 USB 调试"...');
      const adb = await webAdbService.connect(targetDevice);
      const name = (targetDevice as any).productName || (targetDevice as any).model || 'Android设备';

      // 获取设备详细信息
      let deviceInfo = null;
      try {
        deviceInfo = await webAdbService.getDeviceInfo(adb);
      } catch { /* ignore */ }

      set({
        device: targetDevice,
        adb,
        isConnected: true,
        deviceName: deviceInfo?.model || name,
        isConnecting: false,
        deviceInfo
      });

      // 保存已连接的设备
      get().saveConnectedDevice(targetDevice.serial, name);

      useLogStore.getState().addLog('success', `已连接到设备: ${name} (${targetDevice.serial})`);

      // 自动加载设备目录到左侧面板
      try {
        const storagePath = await webAdbService.findStoragePath(adb);
        useFileStore.getState().setPath('left', `adb://${storagePath}`);
        const deviceFiles = await webAdbService.findFiles(adb, storagePath, ['*']);
        useFileStore.getState().setFiles('left', deviceFiles);
        useLogStore.getState().addLog('info', `已加载设备目录: ${storagePath}`);
      } catch (e: any) {
        useLogStore.getState().addLog('warning', `加载设备目录失败: ${e.message}`);
      }
    } catch (error: any) {
      console.error('ADB Connection failed:', error);
      set({ isConnecting: false });
      // 翻译常见错误信息
      let msg = error.message;
      if (msg.includes('already in use') || msg.includes('already in used')) {
        msg = '设备已被其他程序占用，请关闭 Android Studio、adb.exe、Visual Studio、Chrome 远程调试器、Android 模拟器等程序后重试';
      }
      useLogStore.getState().addLog('error', `连接失败: ${msg}`);
      throw error;
    }
  },

  disconnect: async () => {
    const { adb, deviceName } = get();

    if (adb) {
      try {
        await adb.close();
      } catch { /* ignore */ }
      useLogStore.getState().addLog('info', `已断开与设备 ${deviceName} 的连接`);
    }
    set({
      device: null,
      adb: null,
      isConnected: false,
      deviceName: '未连接',
      deviceInfo: null,
    });
  },

  checkConnection: async () => {
    const { adb, isConnected } = get();
    if (!adb || !isConnected) return false;

    try {
      await webAdbService.executeShell(adb, 'echo 1', 2000);
      return true;
    } catch (e) {
      console.warn('Connection lost:', e);
      get().disconnect();
      useLogStore.getState().addLog('warning', '与设备的连接已断开');
      return false;
    }
  },

  autoConnect: async () => {
    try {
      const pairedDevices = await webAdbService.getPairedDevices();
      if (pairedDevices && pairedDevices.length > 0) {
        // 优先连接已保存的设备
        const savedDevices = get().savedDevices;
        const savedSerial = savedDevices.map(d => d.serial);
        const deviceToConnect = pairedDevices.find(d => savedSerial.includes(d.serial)) || pairedDevices[0];
        
        useLogStore.getState().addLog('info', `发现已配对设备: ${deviceToConnect.serial}，正在尝试自动连接...`);
        await get().connect(deviceToConnect);
      }
    } catch (error) {
      console.warn('Auto-connect failed:', error);
    }
  },

  reset: () => set({
    device: null,
    adb: null,
    isConnected: false,
    deviceName: '未连接',
    isConnecting: false,
    savedDevices: getSavedDevices(),
    deviceInfo: null
  }),
}));
